/**
 * Hide elements that are marked with `data-hide-if-empty="true"` when they contain no meaningful content.
 * The check runs after DOMContentLoaded and whenever a section is reloaded in the theme editor.
 */
const PLACEHOLDER_TEXTS = [
  'Image with text',
  'Image banner',
  'Card grid',
  'Card title',
  'Heading',
  'Text',
  'Subheading',
  'Newsletter',
  'Testimonials',
  'Timeline',
  'Placeholder',
  'Collections',
  'Collection',
  'Collection Title',
  'Collection browsing',
  'Your title',
  'Your text',
  'Example Featured Blog Title',
  'By Username',
  'Jan 01, 2022',
  'Lorem tag',
  'Enter text or add dynamic source',
  'Use this text to share brand information with your customers. Introduce a new product, share an announcement or welcome customers.',
  'Add a short description for context or a promotion.',
  'Add supporting content here.',
  'Add customer reviews and testimonials to showcase your store\'s happy customers.',
  'Add your left column description content here.',
  'Add your right column description content here.',
  'Address list',
  'About our store',
  'Author\'s Name',
  'Be the first to know about new collections and exclusive offers.',
  'Blog posts list',
  'Brooklyn, NY, USA',
  'Browse all products',
  'Button label',
  'Button Label',
  'Shop now',
  'Subscribe',
  'Check now',
  'Collapsible content',
  'Icon with text',
  'Contact form',
  'Contact us',
  'Content collage',
  'Countdown banner',
  'Custom HTML',
  'Custom Liquid',
  'Custom dropdown label',
  'Custom text field label',
  'Event is finished',
  'Featured collection',
  'Featured product',
  'Follow us',
  'Footer menu',
  'Frequently bought together',
  'Get direction',
  'Get ready',
  'Hero image with text',
  'Highlight text',
  'Highlight content',
  'Highlight your new collection or share content related to this image',
  'Image column',
  'Image with text columns',
  'Item heading',
  'Item Heading',
  'Left subheading',
  'Logo list',
  'Loyal Customer',
  'Map',
  'More in this topic',
  'Popup',
  'Notify me',
  'On this page',
  'Opening soon',
  'Our collections',
  'Primary action',
  'Product hotspots',
  'Promotional image',
  'Promotions, new products and sales. Directly to your inbox.',
  'Quick links',
  'Right subheading',
  'Secondary action',
  'See all collections',
  'See all products',
  'Send message',
  'Share information about your products, policies or services.',
  'Share information about your promotion.',
  'Short supporting copy for this card.',
  'Size guide',
  'Slide 1',
  'Slideshow about our brand',
  'Store name',
  'Street, City, Country, State',
  'Talk about your shop.',
  'Tell your story',
  'The page URL might be outdated or contain typo errors',
  'This event is finished.',
  'This is a heading',
  'This text area can be used to explain your brand\'s vision and mission to your customers.',
  'Use this for a short annoucement',
  'Video banner',
  'Video with text',
  'View all',
  'Visit our home page',
  'We\'re sorry, this page is not available',
  'Welcome text',
  'Write a short sentence about what customers can expect from subcribing your newsletter.',
  'Write a short sentence about what customers can expect from subscribing your newsletter.',
  'Write about your physical store locations, opening hours, etc.',
  'Write content about your store',
  'You might also like'
].map((text) => text.trim());

const UNIQUE_PLACEHOLDER_TEXTS = [...new Set(PLACEHOLDER_TEXTS.filter(Boolean))];

const TARGET_SELECTOR = '[data-hide-if-empty="true"], .hide-empty';

const isGlobalHideEnabled = () => document.body?.dataset.hideEmptyContent === 'true';

const ELEMENT_NODE = 1;
const DOCUMENT_NODE = 9;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\$&');

const stripPlaceholderText = (text) => {
  if (!text) {
    return '';
  }

  return UNIQUE_PLACEHOLDER_TEXTS.reduce((result, placeholder) => {
    const source = escapeRegExp(placeholder);
    const needsBoundary = /^[\w\s]+$/.test(placeholder);
    const pattern = needsBoundary ? new RegExp(`\\b${source}\\b`, 'gi') : new RegExp(source, 'gi');

    return result.replace(pattern, ' ');
  }, text);
};

const hasMeaningfulMedia = (el) =>
  Array.from(el.querySelectorAll('img,video,iframe,source,svg')).some((media) => {
    if (media.closest('[data-placeholder="true"]')) {
      return false;
    }

    const className = (media.getAttribute('class') || '').toLowerCase();
    return !className.includes('placeholder');
  });

const storeOriginalDisplay = (el) => {
  if (!el.hasAttribute('data-empty-original-display')) {
    el.setAttribute('data-empty-original-display', el.style.display || '');
  }
};

const restoreElement = (el) => {
  if (el.getAttribute('data-empty-hidden') === 'true') {
    const originalDisplay = el.getAttribute('data-empty-original-display');
    el.style.display = originalDisplay || '';
    el.removeAttribute('data-empty-hidden');
  }
};

const shouldEvaluateElement = (el) => {
  if (el.hasAttribute('data-hide-if-empty')) {
    return true;
  }

  if (isGlobalHideEnabled()) {
    return true;
  }

  return Boolean(el.closest('[data-hide-if-empty="true"]'));
};

const toggleEmptyElements = (root = document) => {
  root.querySelectorAll(TARGET_SELECTOR).forEach((el) => {
    if (!shouldEvaluateElement(el)) {
      restoreElement(el);
      return;
    }

    const clone = el.cloneNode(true);
    clone
      .querySelectorAll('[data-placeholder="true"]')
      .forEach((placeholder) => placeholder.remove());

    const textContent = stripPlaceholderText(clone.textContent);
    const hasText = textContent.trim().length > 0;
    const hasMedia = hasMeaningfulMedia(el);
    const shouldHide = !hasText && !hasMedia;

    if (shouldHide) {
      storeOriginalDisplay(el);
      el.style.display = 'none';
      el.setAttribute('data-empty-hidden', 'true');
    } else {
      restoreElement(el);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  toggleEmptyElements();
});

document.addEventListener('shopify:section:load', (event) => {
  toggleEmptyElements(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  toggleEmptyElements(event.target);
});

document.addEventListener('shopify:block:deselect', (event) => {
  toggleEmptyElements(event.target);
});

document.addEventListener('shopify:section:select', (event) => {
  toggleEmptyElements(event.target);
});

document.addEventListener('shopify:section:deselect', (event) => {
  toggleEmptyElements(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  toggleEmptyElements();
});

document.addEventListener('shopify:block:load', (event) => {
  toggleEmptyElements(event.target);
});

if (typeof MutationObserver !== 'undefined') {
  const scheduledRoots = new Set();
  const scheduleFrame =
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (callback) => setTimeout(callback, 16);
  let rafId = null;

  const resolveRoot = (root) => {
    if (root && root.nodeType === ELEMENT_NODE) {
      return root.isConnected ? root : document;
    }

    if (root && root.nodeType === DOCUMENT_NODE) {
      return root;
    }

    return document;
  };

  const flush = () => {
    rafId = null;
    scheduledRoots.forEach((root) => {
      toggleEmptyElements(resolveRoot(root));
    });
    scheduledRoots.clear();
  };

  const observer = new MutationObserver((mutations) => {
    let shouldScheduleGlobal = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        if (mutation.target.nodeType === ELEMENT_NODE) {
          scheduledRoots.add(mutation.target);
        } else {
          shouldScheduleGlobal = true;
        }
      }
    });

    if (shouldScheduleGlobal) {
      scheduledRoots.add(document);
    }

    if (rafId === null && scheduledRoots.size > 0) {
      rafId = scheduleFrame(flush);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}
